import type { NonUndefined } from 'utility-types'
import type { JSONSchema7 } from 'json-schema'
import { validate } from 'jsonschema'

import {
    assert,
    Cache,
    decodeFromBase64,
    delay,
    encodeToBase64,
    type EventListener,
    isArrayNotEmpty,
    isArrayOfType,
    type NullableValues,
    objectEntries,
} from '../../../utils'

import {
    Capability,
    CommissioningFileParsingError,
    ConnectwareError,
    ConnectwareErrorType,
    ConnectwareServiceCreationError,
    type CybusDetailedService,
    type CybusServiceForm,
    type CybusServiceParameters,
    type CybusServiceSchema,
    getSecretParameterNames,
    Translation,
} from '../../../domain'

import { type ConnectwareServicesService, ServiceManagementMode, type ServiceManagementRequest, type ServiceValidationArgs } from '../../../application'

import { type ConnectwareHTTPServiceOptions, extractValidatedJson, FetchConnectwareHTTPService, type HttpResponse } from '../Base'
import { extractAlreadyExists, extractCreationError, extractErrors, mapParserErrorsInformation } from './mappers'
import { isMinimalSchemasResponse, isMinimalValidationError } from './types'

const mapSchemaParameters = (properties: NonUndefined<JSONSchema7['properties']>): NullableValues<CybusServiceParameters> =>
    objectEntries(properties).reduce<NullableValues<CybusServiceParameters>>((r, [name, prop]) => {
        /**
         * If possible, retrieve the default value, otherwise, just declare that the value is there
         */
        let defaultValue = typeof prop === 'boolean' || !('default' in prop) || prop.default === undefined ? undefined : prop.default

        if (
            typeof defaultValue !== 'string' &&
            typeof defaultValue !== 'number' &&
            typeof defaultValue !== 'boolean' &&
            !isArrayOfType(defaultValue, (v): v is string | number => typeof v === 'string' || typeof v === 'number')
        ) {
            defaultValue = null
        }

        return { ...r, [name]: defaultValue }
    }, {})

const validationErrorHandler = async (response: HttpResponse): Promise<ConnectwareError> => {
    const { message } = await extractValidatedJson(response, isMinimalValidationError)
    return new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, message)
}

export class ConnectwareHTTPServicesService extends FetchConnectwareHTTPService implements ConnectwareServicesService {
    private readonly schemaCache = new Cache<Promise<JSONSchema7>>()

    protected updateBreathingRoom = 5_000

    constructor (options: ConnectwareHTTPServiceOptions, private readonly changeDoneListeners: Pick<EventListener<void>, 'trigger'>) {
        super(options)
    }

    async parseCommissioningFile (
        content: string,
        mode: ServiceManagementMode
    ): Promise<[schema: CybusServiceSchema, id: CybusServiceForm['id'], parameters: NullableValues<CybusServiceParameters>]> {
        let schema = await this.schemaCache.get(
            () =>
                this.request<'/api/services/parametersSchema', 'POST', JSONSchema7>({
                    capability: Capability.SERVICES_PARAMETERS_READ,
                    method: 'POST',
                    path: '/api/services/parametersSchema',
                    authenticate: true,
                    body: { commissioningFile: encodeToBase64(content) },
                    handlers: {
                        201: async (response) => {
                            const data = await extractValidatedJson(response, isMinimalSchemasResponse)
                            return data.schemas
                        },
                        400: validationErrorHandler,
                        406: async (response) => {
                            const { message } = await extractValidatedJson(response, isMinimalValidationError)
                            const errors = extractErrors({ message })
                            return errors
                                ? new CommissioningFileParsingError(mapParserErrorsInformation(errors))
                                : new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, message)
                        },
                        500: () => new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, 'There was an issue parsing the commissioning file'),
                    },
                }),
            content
        )

        assert(
            schema.properties && schema.required,
            new ConnectwareError(ConnectwareErrorType.MAPPING_ERROR, 'Schema has missing properties or required fields', { content, schema })
        )

        /**
         * The schema is a very complex json, see `json-schema`
         * But for what is relevant for the admin UI, only `ServiceSchema` is enough
         */
        const { id, ...values } = mapSchemaParameters(schema.properties)

        assert(
            typeof id === 'string',
            new ConnectwareError(ConnectwareErrorType.MAPPING_ERROR, 'Service default id generated from file is not the expected string', { content, schema })
        )

        /**
         * If the parsing is in update mode
         * Then delete the id field from the output schema
         *
         * Secret (write-only) parameters are also optional on update:
         * an omitted value means "keep the stored one"
         */
        if (mode === ServiceManagementMode.UPDATE) {
            const secretNames = new Set(getSecretParameterNames(schema))

            schema = {
                ...schema,
                properties: objectEntries(schema.properties).reduce((r, [f, v]) => (f === 'id' ? r : { ...r, [f]: v }), {}),
                required: schema.required.filter((r) => r !== 'id' && !secretNames.has(r)),
            }
        }

        return [schema as CybusServiceSchema, id, values]
    }

    async manage ({ commissioningFile, id, parameters }: ServiceManagementRequest, mode: ServiceManagementMode): Promise<void> {
        assert(id, new ConnectwareServiceCreationError(Translation.INVALID_SERVICE_ID))

        const body = { commissioningFile: encodeToBase64(commissioningFile), parameters } as const

        switch (mode) {
            case ServiceManagementMode.CREATION:
                await this.request<'/api/services', 'POST', void>({
                    capability: Capability.SERVICES_CREATE_OR_UPDATE,
                    method: 'POST',
                    path: '/api/services',
                    authenticate: true,
                    body: { id, ...body },
                    handlers: {
                        201: () => Promise.resolve(),
                        400: validationErrorHandler,
                        406: async (response) => {
                            const data = await response.getJson()
                            if (extractAlreadyExists(data)) {
                                return new ConnectwareServiceCreationError(Translation.SERVICE_ID_ALREADY_USED)
                            }
                            const creationErrorMessage = extractCreationError(data)
                            if (creationErrorMessage !== null) {
                                return new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, creationErrorMessage)
                            }
                            return new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, data.message)
                        },
                        409: () => new ConnectwareServiceCreationError(Translation.SERVICE_WITH_ID_STILL_BEING_DELETED),
                        500: () => new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, 'There was an issue creating the service'),
                    },
                })
                break
            case ServiceManagementMode.UPDATE:
                await this.request<'/api/v2/services/+', 'PUT', void>({
                    capability: Capability.SERVICES_CREATE_OR_UPDATE,
                    method: 'PUT',
                    path: '/api/v2/services/+',
                    pathParams: [id],
                    authenticate: true,
                    body,
                    handlers: {
                        202: () => Promise.resolve(),
                        400: () => new ConnectwareError(ConnectwareErrorType.BAD_REQUEST, 'Unexpected bad request detected'),
                        406: async (response) => {
                            const { errors } = await response.getJson()
                            return new CommissioningFileParsingError(mapParserErrorsInformation(errors.flatMap((e) => e.parserErrors)))
                        },
                        500: () => new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, 'There was an issue updating the service'),
                    },
                })

                /**
                 * Due to updating being done asynchronously
                 * This makes sure the UI waits a bit before fetching information
                 */
                await delay(this.updateBreathingRoom)

                break
        }

        this.changeDoneListeners.trigger()
    }

    validate ({ schema, id, parameters }: ServiceValidationArgs, mode: ServiceManagementMode): boolean {
        const result = validate(mode === ServiceManagementMode.CREATION ? { ...parameters, id } : parameters, schema)
        return !isArrayNotEmpty(result.errors)
    }

    fetchCommissioningFile (id: CybusDetailedService['id']): Promise<string> {
        return this.request<'/api/v2/services/+/serviceCommissioningFile', 'GET', string>({
            capability: Capability.SERVICE_COMMISSIONING_FILE_READ,
            method: 'GET',
            path: '/api/v2/services/+/serviceCommissioningFile',
            authenticate: true,
            pathParams: [id],
            handlers: {
                200: async (response) => {
                    const { data } = await response.getJson()
                    return decodeFromBase64(data.scf)
                },
                400: () => new ConnectwareError(ConnectwareErrorType.BAD_REQUEST, 'Unexpected bad request detected', { id }),
                404: () => new ConnectwareError(ConnectwareErrorType.NOT_FOUND, 'Service not found', { id }),
                500: () => new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, 'Internal Server Error'),
            },
        })
    }
}
